import {
  Error as AweError,
  DomainSpecificInfo,
  Element,
  EventMap,
  Feature,
} from '@blk/aladdin-graph-everything/platform/event_logging/awe_event/v1/awe_event_pb';
import { AdobeConfig, ApplicationNames } from '@blk/aw-bff-typescript-models';
import type { BaseSyntheticEvent } from 'react';
import { TrackedAction } from '../constants';
import { AdobeAnalyticsInfo } from './AdobeModels';

/**
 * Represents the metadata for the application and the config properties to control logging behavior
 * @tutorial wiki: https://webster.bfm.com/Wiki/display/AFWM/AW+Event+Logging+%28Web%29+-+Developer+Guide#AWEventLogging(Web)DeveloperGuide-Initialisation
 */
export interface AppMetadata {
  adobeConfig?: AdobeConfig; // The Adobe configuration.
  analysisDate?: string; // The analysis date.
  applicationName?: string; // The name of the application, eg. 'AWAdvisor', 'AWVision'
  applicationNameFn?: () => ApplicationNames | undefined; // The function to get the application name.
  appMode?: string; // The application mode.
  atmosUserId?: string; // Atmos user id
  client?: string; // The client name.
  disableUserName?: boolean; // Indicates if the user name is disabled.
  interactiveElementSelectors?: string[]; // The selectors for interactive elements.
  internalUserFn?: () => boolean; // The function to check if it is an internal user.
  isDefaultConfig?: boolean; // Indicates if it is the default configuration.
  languagePreferenceFn?: () => string; // The function to get the language preference.
  name?: string; // The name of the application package eg. 'aw-advisor'
  partnerSource?: string; // The partner source if the user session originated from partner portal
  pkg?: string; // The package name.
  postInterval?: number; // The post interval used for sending events periodically
  queueSize?: number; // The size of the queue.
  routeAliases?: Record<string, string>;
  setSessionIdFn?: () => string; // The function to set the session ID.
  showingMyBook?: boolean; // Indicates if "My Book" is being shown.
  toInternalPathFn?: (routeAliases: Record<string, string> | undefined, publicPath: string) => string;
  userFirm?: string; // The user firm
  userId?: string; // The user ID.
  version?: string; // The version of the application.
  viewAsIdFn?: () => string | undefined; // The function to get the view ID.
  webEntityId?: string; // Web entity id from atmos
  webTrackingId?: string; // webTrackingId needed for Adobe Analytics
}

/**
 * Represents the tracking data for logging events.
 */
export interface TrackingData {
  action: TrackedAction; // The action/event being performed.
  actionInfo?: string; // Additional information about the action.
  activeTab?: string; // The active tab in the application.
  activeView?: string; // The active view in the application, eg. 'accounts', 'households', etc.
  additionalInfo1?: string; // Additional information field 1.
  additionalInfo2?: string; // Additional information field 2.
  area?: string; // The area where the action is performed.
  componentName?: string; // The name of the component.
  domainSpecificInfo?: DomainSpecificInfoType; // Domain-specific information, i.e. proposal or deep dive related information
  elementInfo?: ElementInfo; // Information about the element.

  // Adobe Analytics specific fields for new linkName format
  adobeAnalytics?: AdobeAnalyticsInfo; // Adobe-specific tracking information

  error?: Error; // The error object.
  featureInfo?: FeatureInfo; // Information about the feature.
  float1?: number; // Floating field 1.
  float2?: number; // Floating field 2.
  float3?: number; // Floating field 3.
  float4?: number; // Floating field 4.
  queueTimestamp?: string; // ISO 8601 timestamp when the event was added to the queue
  srcElement?: Element | EventTarget | null; // The source element associated with the action.
  srcEvent?: Event | BaseSyntheticEvent; // The source event that triggered the action.
}
/**
 * Feature specific information which contains featureName i.e. 'risk-monitor', 'proposals', etc.
 * Also contains the section information which helps to locate origin of the event
 */
export type FeatureInfo = Partial<Feature.AsObject>;

/**
 * Element related information such as id, label, type, etc.
 */
export interface ElementInfo extends Partial<Element.AsObject> {
  elementMetadata?: string; // holds the metadata object in stringified JSON format
}

/**
 * Domain specific information which contains proposal and account related information
 */
export interface DomainSpecificInfoType extends Partial<DomainSpecificInfo.AsObject> {
  additionalAttributes?: { [key: string]: string }; // Additional attributes.
}

/**
 * Error object which contains error code, error message, error stack trace, etc.
 */
export interface Error extends Partial<AweError.AsObject> {
  errorStackTrace?: string; // The error stack trace
}

/**
 * Logger type configurations
 */
export enum LoggerType {
  AFTLogger,
  GenStatsLogger,
  AdobeLogger,
}

/**
 * Event types
 */
export const EventType: EventMap = {
  EVENT_UNSPECIFIED: 0,
  EVENT_CLICK: 1,
  EVENT_SCROLL: 2,
  EVENT_HOVER: 3,
  EVENT_CHANGE: 4,
  EVENT_LOAD: 5,
  EVENT_SUBMIT: 6,
  EVENT_DRAG_AND_DROP: 7,
  EVENT_PASTE: 8,
  EVENT_ERROR: 9,
  EVENT_CREATE: 10,
  EVENT_DELETE: 11,
};

/**
 * Event type mappings
 */
export const EventTypeMap: { [key: string]: EventMap[keyof EventMap] } = {
  click: EventType.EVENT_CLICK,
  scroll: EventType.EVENT_SCROLL,
  hover: EventType.EVENT_HOVER,
  change: EventType.EVENT_CHANGE,
  load: EventType.EVENT_LOAD,
  submit: EventType.EVENT_SUBMIT,
  paste: EventType.EVENT_PASTE,
  error: EventType.EVENT_ERROR,
};
